<template>
    <div class="legend flex items-center space-x-4">
        <div>{{ $t("legend") }}: </div>
        <div v-for="legend in showInLegend" :style="{
            color: colorPalette[legend]
        }"
        class="font-bold">{{ $t(`${legend}s`) }}</div>
    </div>
    <div ref="cyContainer" class="cytoscape-container"></div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import cytoscape from "cytoscape";
import cise from 'cytoscape-cise';
const colorPalette = {
    scope: "rgba(0,255,175, 1)",
    intent: "#80d4ff",
    channel: "#b5ff80",
    dim: "#ff80bf",
    intent_catalogue: "#80d4ff",
    intent_dynamic: "#80d4ff",
}

const showInLegend = ["scope", "intent", "channel", "dim"];

cytoscape.use(cise);
import useOrganizationStore from "@/stores/organization";

const orgStore = useOrganizationStore();

const cyContainer = ref(null);
const props = defineProps({
    organizationSlug: {
        type: String,
        required: true
    },
});

onMounted(async () => {
    if (props.organizationSlug) {
        const { nodes, edges } = await orgStore.fetchOrganizationMap(props.organizationSlug);

        const cy = cytoscape({
            container: cyContainer.value,
            elements: [...nodes, ...edges],
            style: [

                {
                    selector: "edge",
                    "style": {
                        "width": 1,
                        "line-color": "rgba(0,255,175, 0.5)",
                        "curve-style": "bezier",
                        "line-style": (ele) => {
                            const type = ele.data("type");
                            return {
                                channel: "dotted",
                                dim: "dashed",
                            }[type] ?? "solid";
                        },
                        "line-color": (ele) => {
                            const type = ele.data("type");
                            return colorPalette[type] ?? "#ccc";
                        },
                    },
                },
                {
                    selector: 'node[label]',
                    style: {
                        "background-color": "#000",             // white background
                        "border-width": 1,                       // slim border
                        "border-color": (ele) => {
                            const type = ele.data("type");
                            return colorPalette[type] ?? "#ccc";
                        },
                        "color": (ele) => {
                            const type = ele.data("type");
                            return colorPalette[type] ?? "#ccc";
                        },
                        "shape": "roundrectangle",               // minimal rounded rectangle
                        "width": "label",                             // fixed slim width
                        "text-valign": "center",
                        "text-halign": "center",
                        "font-size": 16,
                        "label": "data(label)",                  // optional, can remove if no labels
                        "padding-top": 2,
                        "padding-right": 6,
                        "padding-bottom": 2,
                        "padding-left": 6,
                    },
                },
            ],
        });

        // cy.layout({
        //     name: "cose",
        //     roots: cy.nodes().filter(n => n.data("type") === "scope"),
        //     spacingFactor: 1.5,
        // }).run();
        cy.layout({
            name: 'cise',
            animate: false,
            idealEdgeLength: 100,
            nodeOverlap: 4,
            refresh: 20,
            fit: true,
            padding: 30,
            nodeRepulsion: 8000,
            gravity: 1,
            nestingFactor: 1.2,
            dragCoef: 0.99,
            gravityRangeCompound: 1.5,
            gravityCompound: 1.0,
            gravityRange: 3.8,
            initialEnergyOnIncremental: 0.5,
            clusterPadding: 20,
            randomize: false,
            componentSpacing: 100,  // space between clusters
            avoidOverlap: true,     // prevents nodes from overlapping
            nodeDimensionsIncludeLabels: true,
            // optional: make nodes with type "scope" be cluster centers
            startAngle: 3 / 2 * Math.PI,
            sweep: 2 * Math.PI,
            spacingFactor: 1,
            clockwise: true,
            clusters: (node) => {
                const type = node.data('type');
                // unclustered nodes return null
                return type || null;
            },
        }).run();
    }
});
</script>

<style scoped>
.cytoscape-container {
    width: 100%;
    height: 600px;
    border: 1px solid rgba(192, 192, 192, 0.2);
    border-radius: 8px;
    margin: 1em 0;
}
</style>
